import React, { useEffect, useState, useContext } from "react";
import { BasePageModal } from "@/shared/components/modal/page-modal/base-page-modal";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { BaseRadioButton } from "@/shared/components/radio-button/base-radio-button";
import { SimpleForm } from "@/shared/components/text-box/simple-form";
import { DateForm } from "@/shared/components/text-box/date-form";
import { AmountForm } from "@/shared/components/text-box/amount-form";
import { useViewTrades } from "@/features/accounts/shared/hooks/use-view-trades";
import { useTradesContext } from "@/features/accounts/shared/contexts/trades-context";
import { Dropdown, DropdownOption } from "@/shared/components/dropdown/base-dropdown";
import { useConditionalSearch } from "@/features/accounts/shared/hooks/use-conditional-search";
import { Checkbox } from "@/shared/components/check-box/check-box"
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { ErrorDialogContext } from "@/shared/context/error-context";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";
import { utilities } from "@/shared/utils/utility";


interface Props {
  onTapCompleteModal: () => void;
  onTapCloseModal: () => void;
}

// 明細検索モーダル
export const ConditionalSearchModal= ({ onTapCompleteModal, onTapCloseModal }: Props) => {
  const {
    selectedAccount,
  } = useAccountGroupsContext();
  const {
    setDetails,
    searchConditionsList,
    setSearchConditionsList,
    selectedConditionId,
    setSelectedConditionId,
    searchTradeType,
    setSearchTradeType,
    checkDeposit,
    setCheckDeposit,
    checkWithdrawal,
    setCheckWithdrawal,
    checkNoSpec,
    setCheckNoSpec,
    searchStartDate,
    setSearchStartDate,
    searchEndDate,
    setSearchEndDate,
    searchMinAmount,
    setSearchMinAmount,
    searchMaxAmount,
    setSearchMaxAmount,
    searchMinBalance,
    setSearchMinBalance,
    searchMaxBalance,
    setSearchMaxBalance,
    searchSummaryKeyword,
    setSearchSummaryKeyword,
    searchMemoKeyword,
    setSearchMemoKeyword,
    clearConditionalSearch,
    setStartDateForce,
    setEndDateForce,
    setForceTradesSetDone,
  } = useTradesContext();
  const {
    getTrdaes,
  } = useViewTrades();
  const {
    check,
    conditionName,
    isActiveSearchButton,
    getSearchConditions,
    registerSearchConditions,
    updateSearchConditions,
    deleteSearchConditions,
    changeSearchButtonState,
    onSelectTradeType,
    onTapCheck,
    onChangeTitleForm,
    onChangeSummaryForm,
    onChangeStartDateForm,
    onChangeEndDateForm,
    onChangeMinAmountForm,
    onChangeMaxAmountForm,
    onChangeMinBalanceForm,
    onChangeMaxBalanceForm,
    onChangeMemoForm,
  } = useConditionalSearch();
  const { toYYYYMMDD } = utilities();
  const [selectedOption, setSelectedOption] = useState<DropdownOption | undefined>(undefined);
  const [fetchFlg, setFetchFlg] = useState<number>(0);

  // 検索条件更新
  useEffect(() => {
    const fetchSearchConditions = async () => {
      const res = await getSearchConditions();
      if (res !== undefined) {
        const conditions = res.map(condition => ({
          ...condition,
          isSelected: false,
        }));
        setSearchConditionsList(conditions);
      }
    }
    fetchSearchConditions();
  },[fetchFlg]);

  // 検索条件ドロップダウン表示更新
  useEffect(() => {
    const condition = searchConditionsList.find(item => item.condition_id === selectedConditionId);
    const setDropDownDefault = () => {
      if (condition) {
        setSelectedOption({value: condition.condition_id, label: condition.condition_name });
      } else {
        setSelectedOption(undefined);
      }
    }
    setDropDownDefault();
  }, [searchConditionsList, selectedConditionId]);

  useEffect(() => {
    changeSearchButtonState();
  }, [check, conditionName]);

  if (!selectedAccount) {
    return null;
  }
  const { setError } = useContext(ErrorDialogContext);

  const now = new Date();
  const todayStr = toYYYYMMDD(now);
  const isInvalidDateRange =
    searchStartDate !== "" && searchEndDate !== "" && searchStartDate > searchEndDate;

  const isInvalidDate = 
    searchStartDate !== "" && searchStartDate > todayStr || searchEndDate !== "" && searchEndDate > todayStr;

  const isInvalidAmountRange =
    searchMinAmount !== undefined && searchMaxAmount !== undefined && searchMinAmount > searchMaxAmount;

  const isInvalidBalanceRange =
    searchMinBalance !== undefined && searchMaxBalance !== undefined && searchMinBalance > searchMaxBalance;

  const hasInvalidAmountOrBalanceInput =
    Number.isNaN(searchMinAmount) ||
    Number.isNaN(searchMaxAmount) ||
    Number.isNaN(searchMinBalance) ||
    Number.isNaN(searchMaxBalance);

  const validateSearchInputs = (): boolean => {
    if (isInvalidDateRange) {
      setError(new MoneyLinkWarning({
        title: "入力エラー",
        message: "年月日の入力に誤りがあります。\n左側に入力された日付より後の日付を、右側の入力欄にご入力ください。",
      }));
      return false;
    }

    if (isInvalidDate) {
      setError(new MoneyLinkWarning({
        title: "入力エラー",
        message: "日付の入力に誤りがあります。\n開始日または終了日が今日以降の日付になっています。",
      }));
      return false;
    }

    if (hasInvalidAmountOrBalanceInput) {
      setError(new MoneyLinkWarning({
        title: "入力エラー",
        message: "金額または残高の入力に誤りがあります。\nマイナス記号のみは入力できません。",
      }));
      return false;
    }

    if (isInvalidAmountRange) {
      setError(new MoneyLinkWarning({
        title: "入力エラー",
        message: "金額の入力に誤りがあります。\n左側に入力された金額より高い金額を、右側の入力欄にご入力ください。",
      }));
      return false;
    }

    if (isInvalidBalanceRange) {
      setError(new MoneyLinkWarning({
        title: "入力エラー",
        message: "残高の入力に誤りがあります。\n左側に入力された残高より高い残高を、右側の入力欄にご入力ください。",
      }));
      return false;
    }

    return true;
  };

  return (
    <BasePageModal
      title="明細を検索"
      className="md-list-search"
      handleClose={() => {
        onTapCloseModal();
      }}
      bottomChildren={
        <BaseRoundButton
          buttonText="検索"
          className="search"
          onClick={async () => {
            if (!validateSearchInputs()) {
              return;
            }
            setStartDateForce(undefined);
            setEndDateForce(undefined);
            setForceTradesSetDone(false);
            const res = await getTrdaes(
              selectedAccount.accountGroupId,
              selectedAccount.mlinkAccountId,
              selectedAccount.mlinkId,
              selectedAccount.passbookType,
              selectedAccount.industryCode,
              selectedAccount.companyCode,
              (searchStartDate !== "" ? searchStartDate : undefined),
              (searchEndDate !== "" ? searchEndDate : undefined),
            );
            if (res !== undefined) {
              setDetails(res);
            }
            if (check) {
              await registerSearchConditions();
            } else if (selectedConditionId) {
              const selectedCondition = searchConditionsList.find(
                item => item.condition_id === selectedConditionId
              );
              if (selectedCondition) {
                const hasChanged =
                  selectedCondition.trade_type !== searchTradeType ||
                  (selectedCondition.date_start || "") !== searchStartDate ||
                  (selectedCondition.date_end || "") !== searchEndDate ||
                  (selectedCondition.transact_match || "") !== searchSummaryKeyword ||
                  (selectedCondition.amount_low ? Number(selectedCondition.amount_low) : undefined) !== searchMinAmount ||
                  (selectedCondition.amount_high ? Number(selectedCondition.amount_high) : undefined) !== searchMaxAmount ||
                  (selectedCondition.balance_low ? Number(selectedCondition.balance_low) : undefined) !== searchMinBalance ||
                  (selectedCondition.balance_high ? Number(selectedCondition.balance_high) : undefined) !== searchMaxBalance ||
                  (selectedCondition.memo_match || "") !== searchMemoKeyword;
                if (hasChanged) {
                  await updateSearchConditions(selectedConditionId, selectedCondition.condition_name);
                  setFetchFlg(prev => prev + 1);
                }
              }
            }
            onTapCompleteModal();
          }}
          isActive={isActiveSearchButton}
        />
      }
      disableOverlayClose={true}
    >
      <h2>条件を指定して「検索」をクリック</h2>
      <dl>
        <dt>検索条件</dt>
        <dd>
          <div className="form-select">
            <Dropdown
              className="dropdown-search"
              options={
                searchConditionsList.map(item => ({
                  value: item.condition_id,
                  label: item.condition_name,
                }))
              }
              value={selectedOption}
              defaultValue={{value: "__DEFAULT__", label: "検索条件を選択" }}
              onChange={(value) => {
                const selectedCondition = searchConditionsList.find(item => item.condition_id === value);
                if (selectedCondition) {
                  setSelectedConditionId(selectedCondition.condition_id)
                  if (selectedCondition.trade_type === 1) {
                    setCheckDeposit(true);
                    setCheckWithdrawal(false);
                    setCheckNoSpec(false);
                    setSearchTradeType(1); // 1:入金
                  } else if (selectedCondition.trade_type === 2) {
                    setCheckDeposit(false);
                    setCheckWithdrawal(true);
                    setCheckNoSpec(false);
                    setSearchTradeType(2); // 2:出金
                  } else {
                    setCheckDeposit(false);
                    setCheckWithdrawal(false);
                    setCheckNoSpec(true);
                    setSearchTradeType(0); // 0:全て
                  }
                  setSearchStartDate(selectedCondition?.date_start);
                  setSearchEndDate(selectedCondition?.date_end);
                  setSearchMinAmount(selectedCondition?.amount_low ? Number(selectedCondition.amount_low) : undefined);
                  setSearchMaxAmount(selectedCondition?.amount_high ? Number(selectedCondition.amount_high) : undefined);
                  setSearchMinBalance(selectedCondition?.balance_low ? Number(selectedCondition.balance_low) : undefined);
                  setSearchMaxBalance(selectedCondition?.balance_high ? Number(selectedCondition.balance_high) : undefined);
                  setSearchSummaryKeyword(selectedCondition?.transact_match);
                  setSearchMemoKeyword(selectedCondition?.memo_match);
                }
              }}
              />
          </div>
        </dd>

        <dt></dt>
        <dd>
          <Checkbox
            label="現在の検索条件を登録する"
            checked={check}
            handleChange={onTapCheck}
            className="registerSerch"
          />
        </dd>
        {check && (
        <>
        <dt>検索条件名</dt>
        <dd>
          <SimpleForm
            id=""
            className=""
            placeholder="検索条件名を設定（必須）"
            onChange={(value) => {onChangeTitleForm(value)}}
            isDisabled={!check}
          />
        </dd>
        </>
        )}

        <dt>入出金</dt>
        <dd>
          <div id="searchInOut" className="rdo-box">
            <BaseRadioButton
              id="searchIn"
              value="in"
              label="入金"
              name="inout"
              checked={checkDeposit}
              onChange={() => {onSelectTradeType("in")}}
            />
            <BaseRadioButton
              id="searchOut"
              value="out"
              label="出金"
              name="inout"
              checked={checkWithdrawal}
              onChange={() => {onSelectTradeType("out")}}
            />
            <BaseRadioButton
              id="searchAll"
              value="all"
              label="指定なし"
              name="inout"
              checked={checkNoSpec}
              onChange={() => {onSelectTradeType("all")}}
            />
          </div>
        </dd>

        <SimpleForm
          id="searchDetails"
          className="form-1box"
          title="取引内容"
          onChange={(value) => {onChangeSummaryForm(value)}}
          value={searchSummaryKeyword}
          descriptionType={true}
          labelEnable={true}
        />

        <dt>
          <label htmlFor="searchDate1">日付</label>
        </dt>
        <dd className="form-2box">
          <DateForm
            id="searchDate1"
            onChange={(value) => {onChangeStartDateForm(value)}}
            // searchStartDateの右から2文字目と4文字目にハイフンを入れてセット
            value={searchStartDate === "" ? "" : `${searchStartDate.slice(0,4)}-${searchStartDate.slice(4,6)}-${searchStartDate.slice(6,8)}`}
          />
          <span>〜</span>
          <DateForm
            id="searchDate2"
            onChange={(value) => {onChangeEndDateForm(value)}}
            // searchEndDateの右から2文字目と4文字目にハイフンを入れてセット
            value={searchEndDate === "" ? "" : `${searchEndDate.slice(0,4)}-${searchEndDate.slice(4,6)}-${searchEndDate.slice(6,8)}`}
          />
        </dd>

        <dt>
          <label htmlFor="searchPrice1">金額</label>
        </dt>
        <dd className="form-2box">
          <AmountForm
            id="searchPrice1"
            onChange={(value) => {onChangeMinAmountForm(value)}}
            initialValue={searchMinAmount !== undefined ? String(searchMinAmount) : ""}
          />
          <span>〜</span>
          <AmountForm
            id="searchPrice2"
            onChange={(value) => {onChangeMaxAmountForm(value)}}
            initialValue={searchMaxAmount !== undefined ? String(searchMaxAmount) : ""}
          />
        </dd>

        <dt>
          <label htmlFor="searchBalance1">残高</label>
        </dt>
        <dd className="form-2box">
          <AmountForm
            id="searchBalance1"
            onChange={(value) => {onChangeMinBalanceForm(value)}}
            initialValue={searchMinBalance !== undefined ? String(searchMinBalance) : ""}
          />
          <span>〜</span>
          <AmountForm
            id="searchBalance2"
            onChange={(value) => {onChangeMaxBalanceForm(value)}}
            initialValue={searchMaxBalance !== undefined ? String(searchMaxBalance) : ""}
          />
        </dd>
        <SimpleForm
          id="searchSummary"
          className="form-1box"
          title="メモ"
          onChange={(value) => {onChangeMemoForm(value)}}
          value={searchMemoKeyword}
          descriptionType={true}
          labelEnable={true}
          maxLength={500}
          validationRule="lineBreakStandard"
        />
        <nav>
          <BaseRoundButton
            buttonText="クリア"
            className="clear"
            onClick={() => {
              clearConditionalSearch();
              setSelectedOption(undefined);
            }}
          />
          <BaseRoundButton
            buttonText="選択中の検索条件を削除"
            className="delete"
            onClick={() => {
              if (!selectedConditionId) {
                return;
              }
              deleteSearchConditions(selectedConditionId);
              clearConditionalSearch();
              setSelectedOption(undefined);
              setFetchFlg(prev => prev + 1);
            }}
          />
        </nav>
      </dl>
    </BasePageModal>
  );
};
